import PropTypes from "prop-types";
import { memo, useCallback, useContext, useState } from "react";
import { FormattedMessage, FormattedNumber, FormattedPlural } from "react-intl";
import Modal from "react-modal";
import TripAdvisorDetailContainer from "app/pages/FicheProduit/components/TripAdvisor/TripAdvisorDetail/TripAdvisorDetailContainer";
import "./TripAdvisor.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import appGlobalsContext from "app/AppGlobalsContext";
import { roundTripAdvisorRating } from "app/utils/utils";
import classNames from "classnames";
import { CrossIcon } from "app/icons/navigation";

const TripAdvisor = ({
	id,
	rating,
	reviewsCount,
	awardLabel,
	locationName,
	hideIcon = false,
	className,
}) => {
	const { publicPath = "" } = useContext(appGlobalsContext);
	const [isModalOpen, setIsOpen] = useState(false);
	const roundedRating = roundTripAdvisorRating(rating);
	const ratingValue =
		roundedRating.toString().indexOf(".") === -1 ? roundedRating + ".0" : roundedRating;

	const openModal = useCallback(() => {
		setIsOpen(true);
	}, []);
	const closeModal = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<>
			<div
				className={classNames("tripadvisor", className)}
				data-testid="tripadvisor"
				onClick={openModal}
			>
				<div
					className={classNames("tripadvisor__rating", {
						"tripadvisor__rating--hide-logo": hideIcon,
					})}
				>
					{reviewsCount > 0 && (
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							component="span"
							className="tripadvisor__rating-label"
						>
							<FormattedNumber
								value={rating}
								minimumFractionDigits={1}
								maximumFractionDigits={1}
								useGrouping={false}
							/>
						</Typography>
					)}
					<img
						src={`${publicPath}/icons/tripadvisor/${ratingValue}-MCID-5.svg`}
						alt={`Trip advisor rating ${ratingValue}`}
					/>
				</div>

				{reviewsCount > 0 && (
					<div className="tripadvisor__reviews">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.SMALL}
							className="tripadvisor__reviews-label"
							component="div"
						>
							<FormattedPlural
								value={reviewsCount}
								one={
									<FormattedMessage
										id="product.review.count.singular"
										values={{
											reviewsCount: 1,
										}}
									/>
								}
								other={
									<FormattedMessage
										id="product.review.count.plural"
										values={{
											reviewsCount: (
												<FormattedNumber
													value={reviewsCount}
													styles="decimal"
												/>
											),
										}}
									/>
								}
							/>
						</Typography>
						<i className="icon icon--tripadvisor-info tripadvisor__icon-detail flip-if-rlt" />
					</div>
				)}

				{awardLabel && awardLabel}
			</div>

			<Modal
				isOpen={isModalOpen}
				portalClassName="tripadvisor__portal"
				onRequestClose={closeModal}
			>
				<div className="tripadvisor-modal__body">
					<div className="tripadvisor-modal__head">
						<div
							className="tripadvisor-modal__title"
							data-testid="tripadvisor-detail-modal-title"
						>
							{locationName}
						</div>
						<CrossIcon
							height={16}
							width={16}
							className="icon tripadvisor-modal__close"
							data-testid="tripadvisor-detail-modal-close"
							onClick={closeModal}
						/>
					</div>
					<div className="tripadvisor-modal__content">
						<TripAdvisorDetailContainer tripAdvisorId={id} />
					</div>
				</div>
			</Modal>
		</>
	);
};

TripAdvisor.propTypes = {
	id: PropTypes.string,
	locationName: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	rating: PropTypes.number,
	reviewsCount: PropTypes.number,
	awardLabel: PropTypes.element,
	hideIcon: PropTypes.bool,
};

export default memo(TripAdvisor);
